/**
 * Template Engine Main Export
 * Central hub for all template engine functionality
 */

// Export types
export type {
  NamingConvention,
  TemplateRenderContext,
  TemplateField,
  RenderedTemplate,
  ExportTemplate,
  FileTemplate
} from '../../types/exportTemplates';

export type { StringsItem } from '../stores/stringsStore';

// Export core utilities
export { applyNamingConvention } from './core/naming';
export { 
  escapeLuaString,
  stripHtmlTags,
  extractTextValue,
  extractStringIds,
  formatValue,
  processVariableReferencesForExport
} from './core/formatting';
export { renderTemplate, renderExportTemplate } from './core/templateRendering';

// Export field mapping utilities
export { mapBlueprintField, extractFields } from './fieldMapping/extraction';
export { 
  resolveReferenceById,
  applyFieldExportMapping
} from './fieldMapping/references';
export type { LookupContext, FieldExportResult } from './fieldMapping/references';
export {
  applyTextCase,
  parseTextCaseFromExportBehavior,
  processFieldValue,
  groupItemsByCategory
} from './fieldMapping/processing';

// Export structure builders
export { buildFolderBasedStructure } from './structure/folderBased';
export type { FolderNode, ItemFieldsNode } from './structure/folderBased';

// Export Unity renderers - existing
export { getCSharpType, formatValueCSharp, generateReferencePath } from './renderers/unity/helpers';
export { renderUnityDatabaseTemplate } from './renderers/unity/database';

// Export Unity renderers - newly migrated
export { renderFolderBasedCSharpTemplate } from './renderers/unity/folderBased';
export { renderRuntimeDatabaseCSharpTemplate } from './renderers/unity/runtime';
export { renderIdBasedRecordsCSharpTemplate } from './renderers/unity/idRecords';
export { renderTypesFile } from './renderers/unity/types';
export { renderTagsFile } from './renderers/unity/tags';
export { renderVariablesFile } from './renderers/unity/variables';
export { renderRuntimeVariablesFile } from './renderers/unity/runtimeVariables';
export { renderTranslationKeysFile } from './renderers/unity/translationKeys';

// Export Runtime Abstraction Layer generators (engine-independent)
export {
  renderGrimoireLogFile,
  renderGrimoireJsonFile,
  renderGrimoireStorageFile,
  renderUnityBootstrapFile,
  renderGodotBootstrapFile,
  renderRuntimeLayerBundle
} from './renderers/unity/runtimeLayer';

// Export Roblox renderers - existing
export { renderRobloxDatabaseTemplate } from './renderers/roblox/database';

// Export Roblox renderers - newly migrated
export { renderFolderBasedLuaTemplate } from './renderers/roblox/folderBased';

// Export Dialog renderers - newly migrated
export { renderDialogsDatabaseTemplate, renderDialogDatabaseFile } from './dialogs/dialogDatabase';
export { renderDialogVariablesFile } from './dialogs/dialogVariables';

// Export JSON renderers
export { renderJsonDatabaseTemplate } from './renderers/json/database';
export { renderFolderBasedJsonTemplate } from './renderers/json/folderBased';
export { renderJsonDialogsFile } from './renderers/json/dialogs';
export {
  renderJsonTranslationsFile,
  renderJsonTypesFile,
  renderJsonTagsFile,
  renderJsonVariablesFile,
  renderJsonDialogVariablesFile
} from './renderers/json/extras';

// Import types for router functions
import { ExportTemplate, NamingConvention } from '../../types/exportTemplates';
import type { StringsItem } from '../stores/stringsStore';

// Import renderers for router functions
import { renderUnityDatabaseTemplate } from './renderers/unity/database';
import { renderRobloxDatabaseTemplate } from './renderers/roblox/database';
import { renderJsonDatabaseTemplate } from './renderers/json/database';
import { renderFolderBasedCSharpTemplate } from './renderers/unity/folderBased';
import { renderRuntimeDatabaseCSharpTemplate } from './renderers/unity/runtime';
import { renderIdBasedRecordsCSharpTemplate } from './renderers/unity/idRecords';
import { renderFolderBasedLuaTemplate } from './renderers/roblox/folderBased';
import { renderFolderBasedJsonTemplate } from './renderers/json/folderBased';
import { renderDialogsDatabaseTemplate } from './dialogs/dialogDatabase';

/**
 * Render database template - routes to appropriate renderer based on platform
 */
export function renderDatabaseTemplate(
  template: ExportTemplate,
  items: any[],
  blueprints: any[],
  types?: any[],
  tags?: any[],
  allItemsForLookup?: any[]
): string {
  // Route to appropriate renderer based on platform
  if (template.platform === 'unity') {
    return renderUnityDatabaseTemplate(template, items, blueprints, types, tags, allItemsForLookup);
  }
  
  if (template.platform === 'json') {
    return renderJsonDatabaseTemplate(template, items, blueprints, types, tags, allItemsForLookup);
  }
  
  // Default to Roblox/Lua for other platforms
  return renderRobloxDatabaseTemplate(template, items, blueprints, types, tags, allItemsForLookup);
}

/**
 * Render folder-based database template
 * Routes to appropriate renderer based on platform and template type
 * 
 * @param template - The export template
 * @param items - Items to export
 * @param blueprints - Available blueprints
 * @param folderPathResolver - Function to resolve folder IDs to paths
 * @param types - Optional types data
 * @param tags - Optional tags data
 * @param allItemsForLookup - Optional all items for lookup
 * @param namingConventionOverride - Optional naming convention override
 * @param customNamespace - Optional custom namespace (C# only)
 * @param customClassName - Optional custom class/table name
 * @param stringsItems - Optional translation strings
 * @param dialogs - Optional dialogs array
 * @param documents - Optional documents array for document field resolution
 */
export function renderFolderBasedDatabaseTemplate(
  template: ExportTemplate,
  items: any[],
  blueprints: any[],
  folderPathResolver: (folderId: string | null | undefined) => string,
  types?: any[],
  tags?: any[],
  allItemsForLookup?: any[],
  namingConventionOverride?: NamingConvention,
  customNamespace?: string,
  customClassName?: string,
  stringsItems?: StringsItem[],
  dialogs?: any[],
  documents?: any[],
  gameLanguages?: string[]
): string {
  // Check for ID-based Records pattern
  if (template.id === 'preset-unity-id-records') {
    return renderIdBasedRecordsCSharpTemplate(
      items, template, blueprints, folderPathResolver, 
      types, tags, allItemsForLookup, namingConventionOverride,
      customNamespace, customClassName, stringsItems, dialogs, documents,
      gameLanguages
    );
  }
  
  // Check for Runtime Content Database pattern
  if (template.id === 'preset-unity-runtime-database') {
    return renderRuntimeDatabaseCSharpTemplate(
      items, template, blueprints, folderPathResolver,
      types, tags, allItemsForLookup, namingConventionOverride,
      customNamespace, customClassName, stringsItems, dialogs, documents,
      gameLanguages
    );
  }
  
  // Create a strings map for the dialogs renderer
  const stringsMap = new Map<string, StringsItem>();
  if (stringsItems) {
    stringsItems.forEach(stringItem => {
      stringsMap.set(stringItem.id, stringItem);
    });
  }
  
  if (template.platform === 'json') {
    return renderFolderBasedJsonTemplate(
      items, template, blueprints, folderPathResolver,
      types, tags, allItemsForLookup, namingConventionOverride,
      customClassName, dialogs, documents
    );
  }
  
  if (template.platform === 'unity') {
    // For Unity C# folder-based template, we need to pass the dialogs renderer
    const fieldConvention = namingConventionOverride || template.namingConventions.fieldName;
    
    return renderFolderBasedCSharpTemplate(
      items, template, blueprints, folderPathResolver,
      types, tags, allItemsForLookup, namingConventionOverride,
      customNamespace, customClassName, stringsItems, dialogs,
      // Pass the dialogs renderer function
      dialogs && dialogs.length > 0 ? (
        (dialogsArray, resolver, sMap, convention) => 
          renderDialogsDatabaseTemplate(dialogsArray, resolver, sMap, convention)
      ) : undefined,
      documents,
      gameLanguages
    );
  }
  
  // Default to Lua for Roblox and other platforms
  return renderFolderBasedLuaTemplate(
    items, template, blueprints, folderPathResolver,
    types, tags, allItemsForLookup, namingConventionOverride,
    customClassName, dialogs, documents
  );
}
